import React, { useEffect } from "react";
import { EditorNavHeader } from "../components/common/EditorNavHeader";
import { NavigationBlocker } from "../components/common/NavigationBlocker";
import {
  useButtonEditorStore,
  selectButtonIsDirty,
} from "../store/useButtonEditorStore";
import {
  getButtonAnimation,
  type ButtonAnimationType,
} from "@codway-dev/shared";
import { useShopifySaveBar } from "../hooks/useShopifySaveBar";
import { SliderRow } from "../components/editor/panels/SettingRows";

// ---------- Preview SVG Icons ----------

const PREVIEW_ICON_SVGS: Record<string, React.ReactNode> = {
  cart: (
    <>
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
    </>
  ),
  "cart-up": (
    <>
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
      <path d="M12 7V3m0 0l-2 2m2-2l2 2" />
    </>
  ),
  bag: (
    <>
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <line x1="3" x2="21" y1="6" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </>
  ),
  "cart-sale": (
    <>
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
      <path d="M15 5l-6 6m0-5h.01M15 11h.01" />
    </>
  ),
  "credit-card": (
    <>
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </>
  ),
  delivery: (
    <>
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
      <path d="M15 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 13.52 9H14" />
      <circle cx="17" cy="18" r="2" />
      <circle cx="7" cy="18" r="2" />
    </>
  ),
  "arrow-right": (
    <>
      <line x1="5" x2="19" y1="12" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </>
  ),
  money: (
    <>
      <rect width="20" height="12" x="2" y="6" rx="2" />
      <circle cx="12" cy="12" r="2" />
      <path d="M6 12h.01M18 12h.01" />
    </>
  ),
  "arrow-down": (
    <>
      <line x1="12" x2="12" y1="5" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </>
  ),
};

// ---------- Helpers ----------

function getShadow(level: number): string {
  switch (level) {
    case 1:
      return "0 1px 3px rgba(0,0,0,0.12)";
    case 2:
      return "0 4px 6px rgba(0,0,0,0.1)";
    case 3:
      return "0 10px 15px rgba(0,0,0,0.1)";
    case 4:
      return "0 20px 25px rgba(0,0,0,0.15)";
    case 5:
      return "0 25px 50px rgba(0,0,0,0.25)";
    default:
      return "none";
  }
}

const ICON_OPTIONS = [
  { value: "none", iconType: "disabled", label: "Ninguno" },
  { value: "cart", iconType: "cart", label: "Carrito" },
  { value: "cart-up", iconType: "cart-up", label: "Carrito lleno" },
  { value: "bag", iconType: "bag", label: "Bolsa" },
  { value: "cart-sale", iconType: "cart-sale", label: "Descuento" },
  { value: "credit-card", iconType: "credit-card", label: "Tarjeta" },
  { value: "delivery", iconType: "delivery", label: "Camión" },
  { value: "arrow-right", iconType: "arrow-right", label: "Flecha Derecha" },
  { value: "money", iconType: "money", label: "Carrito abajo" },
  { value: "arrow-down", iconType: "arrow-down", label: "Flecha Abajo" },
];

// ---------- Component ----------

export default function ButtonEditorPage() {
  const settings = useButtonEditorStore((s) => s.settings);
  const updateSetting = useButtonEditorStore((s) => s.updateSetting);
  const isDirty = useButtonEditorStore(selectButtonIsDirty);
  const isSaving = useButtonEditorStore((s) => s.isSaving);
  const isLoading = useButtonEditorStore((s) => s.isLoading);
  const error = useButtonEditorStore((s) => s.error);
  const save = useButtonEditorStore((s) => s.save);
  const discard = useButtonEditorStore((s) => s.discardChanges);
  const loadFromBackend = useButtonEditorStore((s) => s.loadFromBackend);

  useEffect(() => {
    loadFromBackend();
  }, [loadFromBackend]);

  const previewStyle: React.CSSProperties = {
    backgroundColor: settings.bg_color,
    color: settings.text_color,
    fontSize: `${settings.font_size}px`,
    borderRadius: `${settings.border_radius}px`,
    border:
      settings.border_width > 0
        ? `${settings.border_width}px solid ${settings.border_color}`
        : "none",
    boxShadow: getShadow(settings.shadow),
    padding: "14px 28px",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    fontWeight: 600,
    cursor: "pointer",
    lineHeight: 1.4,
    transition: "all 0.2s ease",
    animation: getButtonAnimation(settings.animation as ButtonAnimationType),
  };

  useShopifySaveBar({ isDirty, isSaving, onSave: save, onDiscard: discard });

  if (isLoading) {
    return (
      <div>
        <EditorNavHeader />
        <s-page heading="Personaliza el Botón de Compra del formulario">
          <s-box padding="large-400">
            <s-stack direction="block" gap="base" alignItems="center">
              <s-spinner />
              <s-text color="subdued">Cargando configuración...</s-text>
            </s-stack>
          </s-box>
        </s-page>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <EditorNavHeader />
        <s-page heading="Personaliza el Botón de Compra del formulario">
          <s-banner tone="critical">
            <s-text>Error al cargar: {error}</s-text>
          </s-banner>
        </s-page>
      </div>
    );
  }

  return (
    <div>
      <EditorNavHeader />
      <NavigationBlocker isDirty={isDirty} />

      <s-page heading="Personaliza el Botón de Compra del formulario">
        <s-section>
          <s-stack direction="inline" gap="base" alignItems="center">
            <s-text type="strong">
              <strong>
                Este es el botón en el que tus clientes harán clic para abrir el
                formulario en las páginas de sus productos o en la página del
                carrito.
              </strong>{" "}
              Personaliza el texto y el diseño de tu botón aquí para adaptarlo
              al estilo de tu sitio web. El botón utilizará la misma fuente que
              tu sitio web cuando se genere en tu tienda.
            </s-text>
          </s-stack>
        </s-section>

        {/* ═══════════════ MAIN GRID ═══════════════ */}
        <s-grid
          gridTemplateColumns="3fr 2fr"
          gap="large-200"
          alignItems="start"
        >
          {/* ──── LEFT: Form Controls ──── */}
          <s-box
            background="base"
            border="small subdued solid"
            borderRadius="large-100"
            padding="large-200"
          >
            <s-stack direction="block" gap="large-200">
              <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                {/* Row 1: Texto del botón | Subtítulo */}
                <s-text-field
                  label="Texto del botón"
                  name="button_text"
                  value={settings.text}
                  placeholder="Pagar Contra Reembolso"
                  onInput={(event: unknown) =>
                    updateSetting(
                      "text",
                      (event as { currentTarget: { value: string } })
                        .currentTarget.value
                    )
                  }
                />
                <s-text-field
                  label="Subtítulo del botón"
                  name="button_subtitle"
                  value={settings.subtitle}
                  placeholder=""
                  onInput={(event: unknown) =>
                    updateSetting(
                      "subtitle",
                      (event as { currentTarget: { value: string } })
                        .currentTarget.value
                    )
                  }
                />

                {/* Row 2: Animación | Ubicación adhesivo */}
                <s-select
                  label="Animación de botón"
                  name="button_animation"
                  value={settings.animation}
                  onChange={(event: any) =>
                    updateSetting("animation", event.target.value)
                  }
                >
                  <s-option value="none">Ninguna</s-option>
                  <s-option value="shake">Sacudida</s-option>
                  <s-option value="bounce">Rebote</s-option>
                  <s-option value="pulse">Pulsación</s-option>
                </s-select>

                <s-select
                  label="Ubicación del botón adhesivo"
                  name="sticky_position"
                  value={settings.sticky_position}
                  onChange={(event: any) =>
                    updateSetting("sticky_position", event.target.value)
                  }
                >
                  <s-option value="bottom">Abajo</s-option>
                  <s-option value="top">Arriba</s-option>
                  <s-option value="none">Desactivado</s-option>
                </s-select>
                <s-color-field
                  label="Color del fondo"
                  name="bg_color"
                  alpha
                  value={settings.bg_color}
                  onInput={(event: any) =>
                    updateSetting("bg_color", event.currentTarget.value)
                  }
                />

                {/* Row 4: Color de texto | Tamaño del texto */}
                <s-color-field
                  label="Color de texto"
                  name="text_color"
                  alpha
                  value={settings.text_color}
                  onInput={(event: any) =>
                    updateSetting("text_color", event.currentTarget.value)
                  }
                />
                <SliderRow
                  layout="stacked"
                  label="Tamaño del texto"
                  value={settings.font_size}
                  min={12}
                  max={24}
                  onChange={(value) => updateSetting("font_size", value)}
                />

                {/* Row 5: Radio del borde | Ancho del borde */}
                <SliderRow
                  layout="stacked"
                  label="Radio del borde"
                  value={settings.border_radius}
                  min={0}
                  max={50}
                  onChange={(value) => updateSetting("border_radius", value)}
                />
                <SliderRow
                  layout="stacked"
                  label="Ancho del borde"
                  value={settings.border_width}
                  min={0}
                  max={10}
                  onChange={(value) => updateSetting("border_width", value)}
                />

                {/* Row 6: Color del borde | Sombra */}
                <s-color-field
                  label="Color del borde"
                  name="border_color"
                  alpha
                  value={settings.border_color}
                  onInput={(event: any) =>
                    updateSetting("border_color", event.currentTarget.value)
                  }
                />
                <SliderRow
                  layout="stacked"
                  label="Sombra"
                  value={settings.shadow}
                  min={0}
                  max={5}
                  unit=""
                  formatValue={(v) => `Nivel ${v}`}
                  onChange={(value) => updateSetting("shadow", value)}
                />
              </s-grid>

              {/* Icon picker (full width) */}
              <s-stack direction="block" gap="small-200">
                <s-text type="strong" color="subdued">
                  Icono de botón
                </s-text>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 40px)",
                    gap: "8px",
                  }}
                >
                  {ICON_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      title={opt.label}
                      onClick={() => updateSetting("icon", opt.value)}
                      style={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 8,
                        border:
                          settings.icon === opt.value
                            ? "2px solid #303030"
                            : "1px solid #ccc",
                        background:
                          settings.icon === opt.value
                            ? "#e3e3e3"
                            : "transparent",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      <s-icon
                        {...({ type: opt.iconType, size: "small" } as any)}
                      />
                    </button>
                  ))}
                </div>
              </s-stack>

              {/* Icon size & color (only when icon is selected) */}
              {settings.icon !== "none" && (
                <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                  <SliderRow
                    layout="stacked"
                    label="Tamaño del icono"
                    value={settings.icon_size}
                    min={16}
                    max={32}
                    onChange={(value) => updateSetting("icon_size", value)}
                  />
                  <s-stack direction="block" gap="small-200">
                    <s-color-field
                      label="Color del icono"
                      name="icon_color"
                      alpha
                      value={settings.icon_color || settings.text_color}
                      onInput={(event: any) =>
                        updateSetting("icon_color", event.currentTarget.value)
                      }
                    />
                    {settings.icon_color && (
                      <s-button
                        variant="tertiary"
                        onClick={() => updateSetting("icon_color", "")}
                      >
                        Restablecer al color del texto
                      </s-button>
                    )}
                  </s-stack>
                </s-grid>
              )}

              {/* Checkbox (full width) */}
              <s-checkbox
                {...({
                  label:
                    "Habilita el botón adhesivo en dispositivos móviles (solo en páginas de productos)",
                  checked: settings.enable_sticky_mobile,
                  onChange: (event: any) =>
                    updateSetting(
                      "enable_sticky_mobile",
                      event.currentTarget.checked
                    ),
                } as any)}
              />
            </s-stack>

            <s-box padding="base" />
            <s-banner tone="info" dismissible>
              <s-stack direction="block" gap="base">
                <strong>¿Necesitas más personalizaciones?</strong>
                <s-text>
                  Si no te gusta{" "}
                  <strong>el Botón de Compra en tu tienda</strong> o te gustaría
                  hacer <strong>otras personalizaciones</strong>, ¡no dudes en
                  contactarnos! ¡Nuestros especialistas te ayudarán a
                  personalizar todo a tu gusto!
                </s-text>
                <s-button icon="chat">Contáctanos</s-button>
              </s-stack>
            </s-banner>
          </s-box>

          {/* ──── RIGHT: Live Preview ──── */}
          <s-box
            background="base"
            border="small subdued solid"
            borderRadius="large-100"
            padding="large-200"
          >
            <s-stack direction="block" gap="large-200">
              <s-text color="subdued">Live preview:</s-text>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button type="button" style={previewStyle}>
                  {settings.icon !== "none" &&
                    PREVIEW_ICON_SVGS[settings.icon] && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={settings.icon_size}
                          height={settings.icon_size}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={settings.icon_color || settings.text_color}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          {PREVIEW_ICON_SVGS[settings.icon]}
                        </svg>
                      </span>
                    )}
                  <span>
                    {settings.text || "Pagar Contra Reembolso"}
                    {settings.subtitle && (
                      <>
                        <br />
                        <span
                          style={{
                            fontSize: "0.75em",
                            fontWeight: 400,
                            opacity: 0.85,
                          }}
                        >
                          {settings.subtitle}
                        </span>
                      </>
                    )}
                  </span>
                </button>
              </div>
            </s-stack>
          </s-box>
        </s-grid>

        <s-box padding="base" />
      </s-page>
    </div>
  );
}
